import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { HttpClient } from 'shared/libs/http';
import { ApiError } from 'shared/types';
import { AsyncState } from 'shared/libs/state';
import {
   PaymentHistoryApiItem,
   PaymentHistoryItem,
   PaymentHistoryResponse,
   PaymentRecordState,
} from './payment-history.types';

export class PaymentHistoryStore {
   readonly loadPaymentHistoryState = new AsyncState<PaymentHistoryResponse>();

   private readonly PER_PAGE = 10;
   rawPaymentHistory: PaymentHistoryItem[] = [];
   currentPage = 1;
   open = false;
   selectedPayment: PaymentHistoryItem | null = null;

   constructor() {
      makeObservable(this, {
         rawPaymentHistory: observable,
         currentPage: observable,
         open: observable,
         selectedPayment: observable,
         totalCount: computed,
         totalPages: computed,
         canPrev: computed,
         canNext: computed,
         loading: computed,
         recordStart: computed,
         recordEnd: computed,
         currentData: computed,
         openPaymentDialog: action.bound,
         closePaymentDialog: action.bound,
         onPrev: action.bound,
         onNext: action.bound,
      });
   }

   get totalCount(): number {
      return this.rawPaymentHistory.length;
   }

   get totalPages(): number {
      return Math.ceil(this.totalCount / this.PER_PAGE);
   }

   get canPrev(): boolean {
      return this.currentPage > 1;
   }

   get canNext(): boolean {
      return this.currentPage < this.totalPages;
   }

   get loading(): boolean {
      return this.loadPaymentHistoryState.status === 'pending';
   }

   get recordStart(): number {
      if (this.totalCount === 0) {
         return 0;
      }

      return (this.currentPage - 1) * this.PER_PAGE + 1;
   }

   get recordEnd(): number {
      if (this.totalCount === 0) {
         return 0;
      }

      return Math.min(this.currentPage * this.PER_PAGE, this.totalCount);
   }

   get currentData(): PaymentHistoryItem[] {
      const startIndex = (this.currentPage - 1) * this.PER_PAGE;
      return this.rawPaymentHistory.slice(startIndex, startIndex + this.PER_PAGE);
   }

   openPaymentDialog(payment: PaymentHistoryItem): void {
      this.selectedPayment = payment;
      this.open = true;
   }

   closePaymentDialog(): void {
      this.selectedPayment = null;
      this.open = false;
   }

   onPrev(): void {
      if (this.canPrev) {
         this.currentPage = this.currentPage - 1;
      }
   }

   onNext(): void {
      if (this.canNext) {
         this.currentPage = this.currentPage + 1;
      }
   }

   async loadPaymentHistory({
      onError,
      onAuthError,
   }: {
      onError?: (message: string) => void;
      onAuthError?: () => void;
   } = {}): Promise<void> {
      await this.loadPaymentHistoryState.run(
         () => HttpClient.post<PaymentHistoryResponse>('/payments/history', null),
         {
            onSuccess: (resp) => {
               if (resp.status === 200) {
                  runInAction(() => {
                     this.rawPaymentHistory = (resp.response ?? []).map((record) =>
                        this.normalizeRecord(record)
                     );
                     this.currentPage = 1;
                  });
                  return;
               }

               if (this.isAuthMessage(resp.message)) {
                  onAuthError?.();
                  return;
               }

               onError?.(resp.message);
            },
            onError: (error) => {
               if (this.shouldTreatAsAuthError(error)) {
                  onAuthError?.();
                  return;
               }

               onError?.(error.message);
            },
         }
      );
   }

   private isAuthMessage(message?: string): boolean {
      if (!message) {
         return false;
      }

      return /token has expired/i.test(message);
   }

   private shouldTreatAsAuthError(error: ApiError): boolean {
      if (this.isAuthMessage(error.message)) {
         return true;
      }

      return error.message === 'No response from server';
   }

   private normalizeRecord(record: PaymentHistoryApiItem): PaymentHistoryItem {
      return {
         ...record,
         state: this.resolveState(record),
      };
   }

   private resolveState(record: PaymentHistoryApiItem): PaymentRecordState {
      if (record.start_date === null && record.end_date === null) {
         return 'PENDING';
      }

      return record.status === 1 ? 'ACTIVATED' : 'DEACTIVATED';
   }
}
